import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2, ChevronRight, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import confetti from 'canvas-confetti';

const SpillTeaPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(false);
  const confettiFired = useRef(false);
  const guidelinesRef = useRef(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/connect-email');
    }
  }, [user, loading, navigate]);

  // If user already has at least 1 review, skip straight to the home page
  useEffect(() => {
    const checkExistingReviews = async () => {
      if (!user) return;
      try {
        const { count, error } = await supabase
          .from('spill_tea_reviews')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);
        if (!error && count > 0) {
          navigate('/spill-tea/home', { replace: true });
        }
      } catch {
        // ignore — let them proceed to company select
      }
    };
    if (user) checkExistingReviews();
  }, [user, navigate]);

  // Load companies from user's resume data (with multi-source fallback)
  useEffect(() => {
    const loadCompanies = async () => {
      if (!user) return;

      let foundCompanies = [];

      // --- Source 1: user_resumes table (primary) ---
      try {
        const { data, error } = await supabase
          .from('user_resumes')
          .select('extracted_data')
          .eq('user_id', user.id)
          .order('uploaded_at', { ascending: false })
          .limit(1)
          .single();

        if (!error && data?.extracted_data) {
          const ed = data.extracted_data;
          // Try the direct companies array first
          if (Array.isArray(ed.companies) && ed.companies.length > 0) {
            foundCompanies = ed.companies;
          }
          // Fallback: extract company names from work_experience entries
          if (foundCompanies.length === 0 && Array.isArray(ed.work_experience)) {
            const names = ed.work_experience
              .map(w => w.company || w.employer || w.organization)
              .filter(Boolean);
            if (names.length > 0) foundCompanies = names;
          }
        }
      } catch (err) {
      }

      // --- Source 2: career_roadmaps table (fallback) ---
      if (foundCompanies.length === 0) {
        try {
          const { data, error } = await supabase
            .from('career_roadmaps')
            .select('form_data')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (!error && data?.form_data?.resume_data) {
            const rd = data.form_data.resume_data;
            if (Array.isArray(rd.companies) && rd.companies.length > 0) {
              foundCompanies = rd.companies;
            }
            // Fallback: extract from work_experience inside resume_data
            if (foundCompanies.length === 0 && Array.isArray(rd.work_experience)) {
              const names = rd.work_experience
                .map(w => w.company || w.employer || w.organization)
                .filter(Boolean);
              if (names.length > 0) foundCompanies = names;
            }
          }
        } catch (err) {
        }
      }

      // De-duplicate & clean
      const unique = [...new Set(foundCompanies.map(c => c.trim()).filter(Boolean))];
      setCompanies(unique);
      setIsLoading(false);
    };

    if (user) {
      loadCompanies();
    }
  }, [user]);

  // Mini confetti burst on page load
  useEffect(() => {
    if (confettiFired.current) return;
    const timer = setTimeout(() => {
      confettiFired.current = true;

      const pastelColors = ['#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF', '#E0BBE4', '#FEC8D8', '#D4A5A5'];

      confetti({
        particleCount: 30,
        spread: 50,
        startVelocity: 20,
        gravity: 0.8,
        scalar: 0.7,
        origin: { x: 0.5, y: 0.25 },
        colors: pastelColors,
        ticks: 90
      });

      confetti({
        particleCount: 10,
        angle: 60,
        spread: 35,
        startVelocity: 16,
        gravity: 0.8,
        scalar: 0.6,
        origin: { x: 0.35, y: 0.25 },
        colors: pastelColors,
        ticks: 70
      });
      confetti({
        particleCount: 10,
        angle: 120,
        spread: 35,
        startVelocity: 16,
        gravity: 0.8,
        scalar: 0.6,
        origin: { x: 0.65, y: 0.25 },
        colors: pastelColors,
        ticks: 70
      });
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{
        position: 'relative',
        fontFamily: "'Montserrat', sans-serif"
      }}
    >
      {/* Fixed background — same blue sky as roadmap page */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/bluewhite.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: -1,
          transform: 'translateZ(0)',
          willChange: 'transform'
        }}
      />

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap');
        `}
      </style>

      {/* Header */}
      <header
        className="sticky top-0 z-40 border-b backdrop-blur-xl"
        style={{
          background: 'rgba(255, 252, 248, 0.65)',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)',
          WebkitBackdropFilter: 'blur(20px)',
          backdropFilter: 'blur(20px)'
        }}
      >
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/roadmap')}
              className="rounded-xl shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '1.3rem' }}>🍵</span>
              <h1
                className="text-xl font-bold"
                style={{ fontFamily: "'Montserrat', sans-serif", color: '#2d1b0e' }}
              >
                Spill the Tea
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Glass container — community feed style */}
        <div
          className="rounded-[32px] overflow-hidden relative"
          style={{
            background: 'linear-gradient(145deg, rgba(200, 210, 220, 0.45) 0%, rgba(180, 195, 210, 0.35) 50%, rgba(170, 185, 200, 0.3) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          }}
        >
          {/* Glass reflection effects */}
          <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-white/30 to-transparent rounded-t-[32px] pointer-events-none" />
          <div className="absolute top-3 left-8 w-32 h-6 bg-white/20 rounded-full blur-lg transform -rotate-6" />

          {/* Header inside glass */}
          <div className="relative px-6 pt-5 pb-4 border-b border-white/20">
            <h3
              className="text-xl md:text-2xl font-black text-white mb-2 tracking-tight drop-shadow-sm"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Leave a Review of a company you've worked at
            </h3>
            <div
              className="space-y-1 text-sm font-medium"
              style={{ fontFamily: "'Inter', sans-serif", color: 'rgba(255,255,255,0.7)' }}
            >
              <p>1. All reviews are anonymous</p>
              <p>2. You can access the entire database of reviews once you leave one review!</p>
            </div>
          </div>

          {/* Company List */}
          <div className="relative px-4 py-4 space-y-3">
            {isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-white/60" />
              </div>
            ) : companies.length === 0 ? (
              <div className="text-center py-16 px-4">
                <span style={{ fontSize: '3rem' }}>🫖</span>
                <h3
                  className="text-lg font-bold text-white mt-4 mb-2"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  No companies found
                </h3>
                <p
                  className="text-white/60 text-sm max-w-sm mx-auto"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Upload a resume with work experience so we can show you the companies you've worked at.
                </p>
                <Button
                  variant="outline"
                  className="mt-6"
                  onClick={() => navigate('/roadmap')}
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: '#1a1a1a',
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: '600',
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  }}
                >
                  Back to Roadmap
                </Button>
              </div>
            ) : (
              <AnimatePresence>
                {companies.map((company, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.07, duration: 0.3 }}
                    onClick={() => {
                      setSelectedCompany(company);
                      setGuidelinesAccepted(false);
                      // Scroll to guidelines after a short delay
                      setTimeout(() => {
                        guidelinesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 150);
                    }}
                    className="group cursor-pointer"
                  >
                    <div
                      className="px-5 py-4 rounded-2xl flex items-center justify-between transition-all duration-200 border"
                      style={{
                        background: selectedCompany === company
                          ? 'rgba(37, 99, 235, 0.12)'
                          : 'rgba(255, 255, 255, 0.75)',
                        borderColor: selectedCompany === company
                          ? 'rgba(37, 99, 235, 0.35)'
                          : 'rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(8px)',
                        boxShadow: selectedCompany === company
                          ? '0 0 0 1px rgba(37, 99, 235, 0.15), 0 4px 12px rgba(37, 99, 235, 0.1)'
                          : 'none',
                      }}
                    >
                      <span
                        className="text-[15px] font-semibold truncate"
                        style={{
                          color: selectedCompany === company ? '#1e40af' : '#1a1a1a',
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        {company}
                      </span>
                      <ChevronRight
                        className={`w-5 h-5 transition-all shrink-0 ml-4 ${
                          selectedCompany === company
                            ? 'text-blue-500 translate-x-0.5'
                            : 'text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5'
                        }`}
                      />
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>

          {/* Guidelines — shown when a company is selected */}
          <AnimatePresence>
            {selectedCompany && companies.length > 0 && (
              <motion.div
                ref={guidelinesRef}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.35, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="px-6 pt-5 pb-6 border-t border-white/20">
                  {/* Section title */}
                  <h4
                    className="text-sm font-bold text-white/90 mb-4 tracking-wide uppercase"
                    style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.06em' }}
                  >
                    Community Guidelines
                  </h4>

                  {/* Guidelines text */}
                  <div
                    className="space-y-3 text-[14.5px] leading-relaxed"
                    style={{ fontFamily: "'Inter', sans-serif", color: 'rgba(255,255,255,0.75)' }}
                  >
                    <p>Reviews may only be posted about companies with 5 or more employees. Do not knowingly post false, misleading, or exaggerated information.</p>
                    <p>Do not include names or identifying details of a specific person. Reviews represent individual opinions and experiences, not statements of fact.</p>
                    <p>We reserve the right to review, edit, or remove any content at our sole discretion. Users may report content they believe violates these guidelines.</p>
                    <p style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.55)' }}>
                      Nothing on this platform should be interpreted as a statement of objective fact.
                    </p>
                  </div>

                  {/* Checkbox */}
                  <div className="mt-5">
                    <label
                      className="flex items-start gap-3 cursor-pointer group"
                      onClick={() => {
                        if (!guidelinesAccepted) {
                          setGuidelinesAccepted(true);
                          // Navigate after a brief moment so the check is visible
                          setTimeout(() => {
                            navigate(`/spill-tea/${encodeURIComponent(selectedCompany)}`);
                          }, 400);
                        }
                      }}
                    >
                      <div
                        className="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all duration-200"
                        style={{
                          borderColor: guidelinesAccepted ? '#2563eb' : 'rgba(255,255,255,0.5)',
                          background: guidelinesAccepted
                            ? 'linear-gradient(135deg, #2563eb, #60a5fa)'
                            : 'rgba(255,255,255,0.85)',
                        }}
                      >
                        {guidelinesAccepted && (
                          <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                        )}
                      </div>
                      <span
                        className="text-sm font-bold"
                        style={{
                          color: guidelinesAccepted ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.8)',
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        I've read and agree to the community guidelines
                      </span>
                    </label>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default SpillTeaPage;
